export function tiersOf(service) {
  const tiers = service?.catalog?.tiers;
  return Array.isArray(tiers) ? tiers : [];
}

export function groupsOf(tier) {
  return Array.isArray(tier?.groups) ? tier.groups : [];
}

export function tasksOf(tier) {
  return groupsOf(tier).flatMap((group) => (Array.isArray(group.tasks) ? group.tasks : []));
}

export function poolOf(service) {
  const stored = Array.isArray(service?.catalog?.groups) ? service.catalog.groups : [];
  const map = new Map();
  stored.forEach((group) => {
    if (group?.id) map.set(group.id, group);
  });
  tiersOf(service).flatMap(groupsOf).forEach((group) => {
    if (group?.id && !map.has(group.id)) map.set(group.id, group);
  });
  return [...map.values()];
}

export function taskCount(service) {
  return poolOf(service).reduce(
    (sum, group) => sum + (Array.isArray(group.tasks) ? group.tasks.length : 0),
    0,
  );
}

/** Included tasks only. Optional add-ons stay off the group total. */
export function groupAmount(group) {
  return (group?.tasks || []).reduce((sum, task) => {
    if (task?.includedByDefault === false) return sum;
    const qty = Math.max(1, Number(task?.quantity) || 1);
    return sum + (Number(task?.price) || 0) * qty;
  }, 0);
}

/** Tier price, or the group prices, or the task prices under it. */
export function tierAmount(tier) {
  const own = Number(tier?.price) || 0;
  if (own > 0) return own;
  const fromGroups = groupsOf(tier).reduce((sum, group) => sum + (Number(group.price) || 0), 0);
  if (fromGroups > 0) return fromGroups;
  return tasksOf(tier).reduce(
    (sum, task) => sum + (Number(task.price) || 0) * (Number(task.quantity) || 1),
    0,
  );
}

export function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
}

const BADGES = [
  [/end of lease|end of tenancy|bond/i, 'End of lease'],
  [/airbnb|turnover|short stay/i, 'Airbnb'],
  [/commerc|commerical|comercial|office|strata|store|shop|building/i, 'Commercial'],
  [/resident|residental|residencial|apartment|domestic|house|unit|\bhome\b/i, 'Residential'],
  [/window/i, 'Windows'],
  [/carpet/i, 'Carpet'],
  [/kitchen/i, 'Kitchen'],
  [/medical|clinic/i, 'Medical'],
  [/deep/i, 'Deep clean'],
];

/** Catalogue badge is the saved category. A known category word in the name is used only when none was saved. */
export function badgeOf(service) {
  const saved = String(service?.category || '').trim();
  if (saved) {
    const hit = BADGES.find(([pattern]) => pattern.test(saved));
    return hit ? hit[1] : (saved.length > 22 ? `${saved.slice(0, 22)}…` : saved);
  }
  const name = String(service?.name || service?.service || '').trim();
  const hit = BADGES.find(([pattern]) => pattern.test(name));
  return hit ? hit[1] : '';
}

export function priceRange(service) {
  const prices = tiersOf(service).map(tierAmount).filter((n) => n > 0);
  if (!prices.length) return '';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? money(min) : `${money(min)} – ${money(max)}`;
}

export function setupPercent(service) {
  const checks = [
    Boolean(String(service?.description || '').trim()),
    Boolean(String(service?.category || '').trim()),
    Boolean(service?.hasImage),
    taskCount(service) > 0,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function blankTask() {
  return {
    id: uid(),
    name: '',
    scope: '',
    instructions: '',
    price: 0,
    quantity: 1,
    inclusion: 'included',
    includedByDefault: true,
  };
}

export function blankGroup(name = 'New group') {
  return { id: uid(), name, price: 0, tasks: [] };
}

export function blankTier(name = '') {
  return { id: uid(), name, code: '', price: 0, groups: [] };
}
